// Google Maps implementation based on Google Maps JavaScript API documentation
class GoogleMapsHandler {
    constructor() {
        this.maps = [];
        this.apiLoaded = false;
        this.init();
    }

    async init() {
        // Find all map containers on the page
        const mapContainers = document.querySelectorAll('#maps-container[data-latitude][data-longitude]');
        
        if (mapContainers.length === 0) {
            return; // No maps to initialize
        }

        try {
            // Load Google Maps API if not already loaded
            if (!this.apiLoaded) {
                await this.loadGoogleMapsAPI();
                this.apiLoaded = true;
            }

            // Initialize each map
            mapContainers.forEach((container, index) => {
                this.initializeMap(container, index);
            });

        } catch (error) {
            console.error('Error loading Google Maps:', error);
        }
    }

    async loadGoogleMapsAPI() {
        return new Promise((resolve, reject) => {
            // Check if Google Maps is already loaded
            if (window.google && window.google.maps) {
                resolve();
                return;
            }

            // Create script element
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${window.GOOGLE_MAPS_API_KEY || ''}&libraries=maps,marker&callback=initGoogleMaps`;
            script.async = true;
            script.defer = true;

            // Set up callback
            window.initGoogleMaps = () => {
                delete window.initGoogleMaps; // Clean up
                resolve();
            };

            script.onerror = () => {
                reject(new Error('Failed to load Google Maps API'));
            };

            document.head.appendChild(script);
        });
    }

    async initializeMap(container, index) {
        const latitude = parseFloat(container.dataset.latitude);
        const longitude = parseFloat(container.dataset.longitude);

        if (isNaN(latitude) || isNaN(longitude)) {
            console.error('Invalid coordinates provided for map:', container);
            return;
        }

        const position = { lat: latitude, lng: longitude };
        
        try {
            // Request needed libraries
            const { Map } = await google.maps.importLibrary("maps");
            const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

            // Create unique map ID for each instance
            const mapId = 'ddf384d92812b824ef7694d1';

            // Initialize the map
            const map = new Map(container, {
                zoom: 10,
                center: position,
                mapId: mapId,
                disableDefaultUI: true,
            });

            // Create inline SVG marker as per Google documentation
            const pinSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            pinSvg.setAttribute('width', '38');
            pinSvg.setAttribute('height', '44');
            pinSvg.setAttribute('viewBox', '0 0 38 44');
            pinSvg.setAttribute('fill', 'none');
            pinSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

            const pinPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            pinPath.setAttribute('fill-rule', 'evenodd');
            pinPath.setAttribute('clip-rule', 'evenodd');
            pinPath.setAttribute('d', 'M6.21299 5.33809C9.63091 1.92017 14.2666 0 19.1003 0C23.934 0 28.5697 1.92017 31.9876 5.33809C35.4055 8.75602 37.3257 13.3917 37.3257 18.2254C37.3257 23.0591 35.4055 27.6948 31.9876 31.1127L19.1003 44L6.21299 31.1127C4.5205 29.4204 3.17792 27.4112 2.26194 25.2C1.34596 22.9888 0.874512 20.6188 0.874512 18.2254C0.874512 15.832 1.34596 13.462 2.26194 11.2508C3.17792 9.03955 4.5205 7.03042 6.21299 5.33809ZM19.1003 23.4324C20.4813 23.4324 21.8057 22.8838 22.7822 21.9073C23.7587 20.9308 24.3073 19.6064 24.3073 18.2254C24.3073 16.8444 23.7587 15.52 22.7822 14.5435C21.8057 13.567 20.4813 13.0184 19.1003 13.0184C17.7193 13.0184 16.3949 13.567 15.4184 14.5435C14.4419 15.52 13.8933 16.8444 13.8933 18.2254C13.8933 19.6064 14.4419 20.9308 15.4184 21.9073C16.3949 22.8838 17.7193 23.4324 19.1003 23.4324Z');
            pinPath.setAttribute('fill', '#00394D');

            pinSvg.appendChild(pinPath);

            // Create the marker with the SVG directly as content (as per Google docs)
            const marker = new AdvancedMarkerElement({
                map: map,
                position: position,
                content: pinSvg,
                title: 'A marker using a custom SVG image.'
            });

            // Store map reference
            this.maps.push({ map, marker, container });

            console.log(`Map ${index + 1} initialized successfully`);

        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }

    // Method to update a map's location
    updateMapLocation(mapIndex, latitude, longitude) {
        if (this.maps[mapIndex]) {
            const position = { lat: latitude, lng: longitude };
            this.maps[mapIndex].map.setCenter(position);
            this.maps[mapIndex].marker.position = position;
        }
    }

    // Method to destroy all maps (useful for cleanup)
    destroyMaps() {
        this.maps = [];
        this.apiLoaded = false;
    }
}

// Export for use in other modules
export { GoogleMapsHandler };

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new GoogleMapsHandler();
});